/**
 * Writes what a clip samples to a push store each frame, and releases it when the clip stops. The
 * AnimationPlayer's bone driver and value driver both run on it. The playhead is the one of the
 * mixer, so the pushed values and the transform tracks agree.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
/**
 * Pushes `clip` to `store` while `isPlaying`. A stop, a deselect, a switch of clip or an unmount
 * releases every key written, so the authored values return.
 */
export function usePushedWrites(store, clip, isPlaying, actionsRef) {
    /** Written only by the frame and `release`: what this clip writes now. */
    const written = useRef(new Map());
    /** Written only by the frame: the next sample. It swaps with `written`, so the hook allocates no map for a frame; the sample of a clip may. */
    const sampled = useRef(new Map());
    /** Written only by the frame and `release`: the playhead the values were sampled at. */
    const sampledAt = useRef(null);
    const release = useCallback(() => {
        // The frame calls this while nothing plays, so an idle player allocates nothing.
        if (written.current.size === 0 && sampledAt.current === null)
            return;
        for (const key of written.current.keys())
            store.set(key, null);
        written.current.clear();
        sampledAt.current = null;
    }, [store]);
    useFrame(() => {
        const action = clip ? actionsRef.current.get(clip.name) : undefined;
        if (!clip || !isPlaying || !action) {
            release();
            return;
        }
        // The live playhead, which the playback loop advanced earlier this frame. A paused clip stands
        // on the values it wrote.
        if (action.time === sampledAt.current)
            return;
        sampledAt.current = action.time;
        const now = sampled.current;
        now.clear();
        clip.sample(action.time, now);
        for (const [key, value] of now)
            store.set(key, value);
        for (const key of written.current.keys()) {
            if (!now.has(key))
                store.set(key, null);
        }
        sampled.current = written.current;
        written.current = now;
    });
    // A stop, a deselect or a switch of clip releases at once, not on the next frame.
    useEffect(() => {
        if (!clip || !isPlaying)
            release();
    }, [clip, isPlaying, release]);
    // A new clip is sampled again, though its playhead has not moved.
    useEffect(() => {
        sampledAt.current = null;
    }, [clip]);
    useEffect(() => release, [release]);
}
//# sourceMappingURL=usePushedWrites.js.map